import { BRAND } from './brand';
import { SECONDARY_CTA_HOW_IT_WORKS, utmCta } from './shared-ctas';
import type { PageCopy } from './page-copy-types';

// ─── /simple-setup ────────────────────────────────────────────────────────
export const SIMPLE_SETUP_COPY: PageCopy = {
  eyebrow: 'Simple Setup — Never miss a call',
  h1: 'Forward your number. AI answers every call. Your business is always on.',
  lede:
    'Forward one phone number to BizRnR. Every inbound call is answered on the first ring by your AI receptionist — in your business voice, with your qualifying script — 24/7/365. Every caller booked on your calendar. Every existing customer reachable any time.',
  ctaPrimary: utmCta({
    label: `${BRAND.primaryCtaLabel} — live in ${BRAND.setupTimeClaim}`,
    plan: 'spark',
    source: 'simplesetup',
    medium: 'page',
    campaign: 'hero',
  }),
  ctaSecondary: SECONDARY_CTA_HOW_IT_WORKS,
  sections: [
    {
      id: 'how-it-works',
      eyebrow: 'How it works',
      heading: `Three steps. ${BRAND.setupTimeClaim}. Zero missed calls.`,
    },
    {
      id: 'step-1',
      heading: 'Sign up',
      body: `Start AI Receptionist for $99/mo. ${BRAND.setupTimeClaim}, cancel anytime, with a 30-day money-back guarantee.`,
    },
    {
      id: 'step-2',
      heading: 'Forward your number',
      body: 'Dial one carrier code — every inbound call rings your AI receptionist.',
    },
    {
      id: 'step-3',
      heading: 'Always on',
      body: 'Bri answers, qualifies the lead, and books onto your calendar.',
    },
  ],
  metadata: {
    title: `Simple Setup — AI Receptionist in ${BRAND.setupTimeClaim} | BizRnR`,
    description: `Forward one phone number. BizRnR answers every call 24/7 in your business voice — books appointments, qualifies leads, sends SMS follow-ups. Setup in ${BRAND.setupTimeClaim}, no code required.`,
    canonical: 'https://bizrnr.com/simple-setup',
  },
};

// ─── /ai-business-page ────────────────────────────────────────────────────
export const AI_BUSINESS_PAGE_COPY: PageCopy = {
  eyebrow: 'AI Business Page — Crawlable business context',
  h1: 'Sign up. Get seen. Your business is always answering.',
  lede: `When a customer asks ${BRAND.aiAssistantsList} “who is the best plumber / dentist / contractor in my city?” — BizRnR gives assistants a structured source about your business. Your AI Business Page goes live the moment you sign up, with a voice widget, chat widget, and AI-readable structured data.`,
  ctaPrimary: utmCta({
    label: 'Go live on the AI grid',
    plan: 'spark',
    source: 'aibusinesspage',
    medium: 'page',
    campaign: 'hero',
  }),
  ctaSecondary: SECONDARY_CTA_HOW_IT_WORKS,
  sections: [
    {
      id: 'how-it-works',
      eyebrow: 'How it works',
      heading: 'Three steps. Ready for AI discovery.',
    },
    {
      id: 'step-1',
      heading: 'Sign up',
      body: 'After the required business details and activation steps are complete, the public page can appear at bizrnr.com/business/yourname with the configured contact experience and structured business data.',
      bullets: [
        'Live page at bizrnr.com/business/yourname',
        'Voice widget + chat widget on every page',
        'LocalBusiness, FAQPage, and SpeakableSpecification JSON-LD already attached',
      ],
    },
    {
      id: 'step-1-summary',
      heading: 'Sign up',
      body: 'A completed, eligible business profile can be published as a crawlable public page.',
    },
    {
      id: 'step-2',
      heading: 'Get seen',
      body: `BizRnR lists your page in sitemap.xml, publishes llms.txt and AI-context entries, and pings supported discovery channels like IndexNow/Bing the moment your page is live.`,
      bullets: [
        'IndexNow + Bing discovery pings on publish',
        'Sitemap, llms.txt, and AI-context entries kept current',
        'Discovery signals refresh whenever you update your page',
      ],
    },
    {
      id: 'step-2-summary',
      heading: 'Get seen',
      body: 'BizRnR publishes crawlable AI context and pings supported discovery channels.',
    },
    {
      id: 'step-3',
      heading: 'Be the answer',
      body: `When a customer asks ${BRAND.aiAssistantsListShort} for a recommendation in your category and city, BizRnR gives assistants accurate, crawlable business facts to evaluate — with the right hours, phone number, and service list.`,
      bullets: [
        'Structured source assistants can cite',
        'Voice widget closes the visitor on-page when they click through',
        'Configured inbound calls can be handled by the AI receptionist workflow',
      ],
    },
    {
      id: 'step-3-summary',
      heading: 'Be the answer',
      body: 'When customers ask AI for a recommendation, your business has a structured source to be evaluated and cited.',
    },
    {
      id: 'step-3-mock-prompt',
      heading: 'Customer asks',
      body: `Hey ChatGPT, who's the best plumber in my city?`,
    },
    {
      id: 'step-3-mock-answer',
      heading: 'AI answers',
      body: 'An assistant may compare current public sources and decide which businesses fit the request.',
      bullets: [
        'Business identity, services, location, hours, and contact details',
        'Current independent reviews or directory information when available',
        'The assistant’s own assessment and cited sources',
      ],
    },
  ],
  metadata: {
    title: 'AI Business Page — Get Found by ChatGPT, Gemini & Perplexity | BizRnR',
    description:
      'BizRnR can publish a crawlable, structured source about an eligible business for search engines and assistants to evaluate; visibility or recommendation is not guaranteed.',
    canonical: 'https://bizrnr.com/ai-business-page',
  },
};
